import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import api from '../api';
import { isChairmanReadOnly } from '../utils/access';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/users';
const MEMBERS_API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/members';

const ROLE_COLORS = { admin: '#c0392b', finance: '#2980b9', secretary: '#27ae60', chairman: '#16a085', credit_officer: '#d35400', member: '#8e44ad' };
const ROLE_LABELS = { admin: 'Admin', finance: 'Finance', secretary: 'Secretary', chairman: 'Chairman', credit_officer: 'Credit Officer', member: 'Member' };
const ROLE_DESC   = {
  admin:     'Full access — all modules, user management, audit log, delete records',
  finance:   'Treasury, Loans, Fines, ROSCA, MIFL, Expenses, Dashboard, Reports, Documents',
  secretary: 'Members, Secretariat, Meetings, Correspondence, Subscriptions, Reports, Documents',
  chairman:  'Full read-only oversight — every module, view-only. No create, edit, or delete access anywhere.',
  credit_officer: 'Treasury, Loans, Financial Dashboard, and Dashboard only — no other modules.',
  member:    'Read-only member portal — own financial data, gallery, secretariat'
};
const OFFICER_TITLES = { admin: 'Finance Chairperson & System Administrator', finance: 'Treasurer', secretary: 'Secretary General' };

export default function UserManagement() {
  const [users, setUsers]           = useState([]);
  const [members, setMembers]       = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editRole, setEditRole]     = useState(null);
  const [resetPwd, setResetPwd]     = useState(null);
  const [sigRow, setSigRow]         = useState(null); // which officer row has sig panel open
  const [msg, setMsg]               = useState('');
  const [msgType, setMsgType]       = useState('success');

  const [createForm, setCreateForm] = useState({ username: '', password: '', role: 'member', member_id: '' });
  const [newRole, setNewRole]       = useState('');
  const [editMember, setEditMember] = useState(null); // which row has the Link Member control open
  const [newMemberId, setNewMemberId] = useState('');
  const [newPwd, setNewPwd]         = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showPwd, setShowPwd]       = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [sigUploading, setSigUploading] = useState(false);
  const sigFileRef = useRef(null);
  const readOnly = isChairmanReadOnly();

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const getCurrentUserId = () => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.id;
    } catch { return null; }
  };
  const currentUserId = getCurrentUserId();

  useEffect(() => {
    fetchUsers();
    axios.get(MEMBERS_API, { headers }).then(r => setMembers(r.data)).catch(() => {});
  }, []);

  const fetchUsers = () => {
    axios.get(API, { headers }).then(r => setUsers(r.data)).catch(() => {});
  };

  const showMessage = (text, type = 'success') => {
    setMsg(text); setMsgType(type);
    setTimeout(() => setMsg(''), 4000);
  };

  const handleMemberSelect = (memberId) => {
    const member = members.find(m => String(m.id) === String(memberId));
    if (member) {
      setCreateForm(prev => ({ ...prev, member_id: memberId, username: `KAMS${member.member_number}`, password: member.phone || '' }));
    } else {
      setCreateForm(prev => ({ ...prev, member_id: '', username: '', password: '' }));
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(API, createForm, { headers });
      showMessage(`Account created for "${createForm.username}"`);
      setCreateForm({ username: '', password: '', role: 'member', member_id: '' });
      setShowCreate(false);
      fetchUsers();
    } catch (err) { showMessage(err.response?.data?.error || 'Failed to create user', 'error'); }
  };

  const changeRole = async (id) => {
    if (!newRole) return;
    try {
      await axios.patch(`${API}/${id}/role`, { role: newRole }, { headers });
      showMessage('Role updated successfully');
      setEditRole(null); setNewRole('');
      fetchUsers();
    } catch (err) { showMessage(err.response?.data?.error || 'Failed', 'error'); }
  };

  // Staff accounts (finance/secretary/chairman/admin) aren't required to
  // link a member at creation — this lets an admin attach or change that
  // link later so the account's real name (via the members JOIN used at
  // login) shows up instead of just the role label. Passing '' unlinks.
  const linkMember = async (id) => {
    try {
      await axios.patch(`${API}/${id}/member`, { member_id: newMemberId || null }, { headers });
      showMessage('Member link updated');
      setEditMember(null); setNewMemberId('');
      fetchUsers();
    } catch (err) { showMessage(err.response?.data?.error || 'Failed', 'error'); }
  };

  const resetPassword = async (id, username) => {
    if (newPwd !== confirmPwd) return showMessage('Passwords do not match', 'error');
    if (newPwd.length < 6) return showMessage('Password must be at least 6 characters', 'error');
    if (!window.confirm(`Reset password for ${username}?`)) return;
    try {
      await axios.patch(`${API}/${id}/password`, { new_password: newPwd }, { headers });
      showMessage(`Password reset for ${username}`);
      setResetPwd(null); setNewPwd(''); setConfirmPwd('');
    } catch (err) { showMessage(err.response?.data?.error || 'Failed', 'error'); }
  };

  const toggleStatus = async (id, username, currentStatus) => {
    const action = currentStatus ? 'deactivate' : 'activate';
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} account for ${username}?`)) return;
    try {
      await axios.patch(`${API}/${id}/status`, { is_active: !currentStatus }, { headers });
      showMessage(`${username} ${action}d`);
      fetchUsers();
    } catch (err) { showMessage(err.response?.data?.error || 'Failed', 'error'); }
  };

  const deleteUser = async (id, username) => {
    if (id === currentUserId) return showMessage('You cannot delete your own account', 'error');
    if (!window.confirm(`Permanently delete user "${username}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/${id}`, { headers });
      showMessage(`User "${username}" deleted`);
      fetchUsers();
    } catch (err) { showMessage(err.response?.data?.error || 'Failed', 'error'); }
  };

  // ── Signature functions ───────────────────────────────────────────────────
  const uploadSignature = async (userId, username) => {
    const file = sigFileRef.current?.files[0];
    if (!file) return showMessage('Select an image file first', 'error');
    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type)) return showMessage('Only JPG, PNG, GIF, WEBP images allowed', 'error');
    if (file.size > 1500000) return showMessage('Image must be under 1.5MB', 'error');

    setSigUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          // Process image through canvas to force white background
          const img = new Image();
          img.onload = async () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            // White base
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0);
            // Remove near-white/light grey background pixels
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;
            for (let i = 0; i < data.length; i += 4) {
              const r = data[i], g = data[i+1], b = data[i+2];
              // Catch light grey, off-white, and light blue-grey paper backgrounds
              if (r > 140 && g > 140 && b > 140) {
                data[i] = 255; data[i+1] = 255; data[i+2] = 255;
              }
            }
            ctx.putImageData(imageData, 0, 0);
            const processedBase64 = canvas.toDataURL('image/png');
            try {
              await axios.patch(`${API}/${userId}/signature`, { signature_image: processedBase64 }, { headers });
              showMessage(`Signature saved for ${username}`);
              setSigRow(null);
              if (sigFileRef.current) sigFileRef.current.value = '';
              fetchUsers();
            } catch (err) {
              showMessage(err.response?.data?.error || 'Upload failed', 'error');
            }
            setSigUploading(false);
          };
          img.src = e.target.result;
        } catch {
          showMessage('Failed to process image', 'error');
          setSigUploading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch {
      showMessage('Failed to read file', 'error');
      setSigUploading(false);
    }
  };

  const removeSignature = async (userId, username) => {
    if (!window.confirm(`Remove signature for ${username}?`)) return;
    try {
      await axios.delete(`${API}/${userId}/signature`, { headers });
      showMessage(`Signature removed for ${username}`);
      fetchUsers();
    } catch (err) { showMessage(err.response?.data?.error || 'Failed', 'error'); }
  };

  const membersWithoutAccounts = members.filter(m => !users.find(u => u.member_id === m.id));

  const inp       = { padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px', width: '100%', boxSizing: 'border-box' };
  const inlineInp = { padding: '6px 8px', border: '1px solid #f39c12', borderRadius: '4px', fontSize: '12px', background: '#fffde7' };
  const btn       = (bg, fg = 'white') => ({ background: bg, color: fg, border: 'none', padding: '7px 14px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' });
  const smBtn     = (bg, fg = 'white') => ({ background: bg, color: fg, border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' });

  const isOfficer = (role) => ['admin', 'finance', 'secretary'].includes(role);

  return (
    <div style={{ padding: '24px', maxWidth: '1100px', margin: '0 auto' }}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <div>
          <h2 style={{ margin: 0, color: '#2c3e50' }}>User Management</h2>
          <p style={{ color: '#888', fontSize: '13px', margin: '4px 0 0' }}>Manage system accounts — roles, passwords, access status, and officer signatures</p>
        </div>
        {!readOnly && <button onClick={() => setShowCreate(!showCreate)} style={btn(showCreate ? '#95a5a6' : '#2c3e50')}>
          {showCreate ? 'Cancel' : '+ New User'}
        </button>}
      </div>

      {msg && (
        <div style={{ padding: '10px 16px', background: msgType === 'error' ? '#fde8e8' : '#d5f5e3', borderRadius: '6px', marginBottom: '16px', fontSize: '13px', color: msgType === 'error' ? '#c0392b' : '#1e8449', borderLeft: `4px solid ${msgType === 'error' ? '#e74c3c' : '#27ae60'}` }}>
          {msg}
        </div>
      )}

      {/* Role Reference */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {Object.entries(ROLE_DESC).map(([role, desc]) => (
          <div key={role} style={{ flex: 1, minWidth: '200px', background: 'white', border: `2px solid ${ROLE_COLORS[role]}`, borderRadius: '8px', padding: '12px 14px' }}>
            <div style={{ marginBottom: '6px' }}>
              <span style={{ background: ROLE_COLORS[role], color: 'white', padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold' }}>
                {ROLE_LABELS[role].toUpperCase()}
              </span>
            </div>
            <div style={{ fontSize: '12px', color: '#555', lineHeight: '1.5' }}>{desc}</div>
          </div>
        ))}
      </div>

      {/* Create User Form */}
      {!readOnly && showCreate && (
        <form onSubmit={createUser} style={{ background: '#f0faf4', border: '1px solid #a9dfbf', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 16px', color: '#1e8449' }}>Create New User Account</h3>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
            <div style={{ minWidth: '180px' }}>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: '#555', marginBottom: '4px' }}>Role *</label>
              <select style={inp} value={createForm.role} onChange={e => setCreateForm({ ...createForm, role: e.target.value, member_id: '', username: '', password: '' })}>
                <option value="member">Member (Portal)</option>
                <option value="finance">Finance</option>
                <option value="secretary">Secretary</option>
                <option value="chairman">Chairman (Read-Only)</option>
                <option value="credit_officer">Credit Officer</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            {createForm.role === 'member' && (
              <div style={{ flex: 1, minWidth: '220px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: '#555', marginBottom: '4px' }}>Select Member *</label>
                <select style={inp} value={createForm.member_id} onChange={e => handleMemberSelect(e.target.value)} required>
                  <option value="">-- Select Member --</option>
                  {membersWithoutAccounts.map(m => (
                    <option key={m.id} value={m.id}>{m.full_name} ({m.member_number})</option>
                  ))}
                </select>
              </div>
            )}
            {createForm.role !== 'member' && (
              <div style={{ flex: 1, minWidth: '220px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: '#555', marginBottom: '4px' }}>Link to Member (optional)</label>
                <select style={inp} value={createForm.member_id} onChange={e => setCreateForm({ ...createForm, member_id: e.target.value })}>
                  <option value="">-- None --</option>
                  {members.map(m => (
                    <option key={m.id} value={m.id}>{m.full_name} ({m.member_number})</option>
                  ))}
                </select>
                <div style={{ fontSize: '10px', color: '#888', marginTop: '3px' }}>So their real name shows in greetings instead of just the role.</div>
              </div>
            )}
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: '#555', marginBottom: '4px' }}>
                Username * {createForm.role === 'member' ? '(auto-filled)' : ''}
              </label>
              <input style={inp} value={createForm.username}
                onChange={e => setCreateForm({ ...createForm, username: e.target.value })}
                placeholder={createForm.role === 'member' ? 'Auto-filled on member select' : 'e.g. treasurer'} required />
            </div>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: '#555', marginBottom: '4px' }}>
                Password * {createForm.role === 'member' ? '(default: phone number)' : '(min 6 characters)'}
              </label>
              <input style={inp} value={createForm.password}
                onChange={e => setCreateForm({ ...createForm, password: e.target.value })}
                placeholder={createForm.role === 'member' ? 'Auto-filled with phone number' : 'Set initial password'}
                required minLength={6} />
            </div>
          </div>
          <div style={{ background: ROLE_COLORS[createForm.role] + '18', border: `1px solid ${ROLE_COLORS[createForm.role]}`, borderRadius: '6px', padding: '8px 12px', marginBottom: '14px', fontSize: '12px', color: '#444' }}>
            <strong>{ROLE_LABELS[createForm.role]} access:</strong> {ROLE_DESC[createForm.role]}
          </div>
          <button type="submit" style={btn('#27ae60')}>Create Account</button>
        </form>
      )}

      {/* Users Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ background: '#2c3e50', color: 'white' }}>
              {['User', 'Role', 'Member', 'Status', 'Created', 'Last Login', 'Actions'].map(h => (
                <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '12px' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr><td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#aaa' }}>No users found</td></tr>
            )}
            {users.map((u, i) => (
              <>
                <tr key={u.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white', borderBottom: sigRow === u.id ? 'none' : '1px solid #eee' }}>

                  <td style={{ padding: '12px' }}>
                    <div style={{ fontWeight: '700', color: '#2c3e50' }}>{u.username}</div>
                    {u.id === currentUserId && <span style={{ fontSize: '11px', color: '#27ae60', fontWeight: '600' }}>● You</span>}
                  </td>

                  <td style={{ padding: '12px' }}>
                    {editRole === u.id ? (
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <select value={newRole} onChange={e => setNewRole(e.target.value)} style={inlineInp}>
                          <option value="">-- Select --</option>
                          <option value="member">Member</option>
                          <option value="finance">Finance</option>
                          <option value="secretary">Secretary</option>
                          <option value="chairman">Chairman</option>
                          <option value="credit_officer">Credit Officer</option>
                          <option value="admin">Admin</option>
                        </select>
                        {!readOnly && <button onClick={() => changeRole(u.id)} style={smBtn('#27ae60')}>Save</button>}
                        <button onClick={() => { setEditRole(null); setNewRole(''); }} style={smBtn('#95a5a6')}>✕</button>
                      </div>
                    ) : (
                      <span style={{ background: ROLE_COLORS[u.role] || '#95a5a6', color: 'white', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                        {ROLE_LABELS[u.role] || u.role}
                      </span>
                    )}
                  </td>

                  <td style={{ padding: '12px', fontSize: '12px', color: '#7f8c8d' }}>
                    {u.member_name ? <span><strong>{u.member_name}</strong><br />{u.member_number}</span> : '—'}
                  </td>

                  <td style={{ padding: '12px' }}>
                    <span style={{ background: u.is_active !== false ? '#d5f5e3' : '#fde8e8', color: u.is_active !== false ? '#1e8449' : '#c0392b', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                      {u.is_active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>

                  <td style={{ padding: '12px', fontSize: '12px', color: '#666' }}>
                    {u.created_at ? new Date(u.created_at).toLocaleDateString('en-UG', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                  </td>

                  <td style={{ padding: '12px', fontSize: '12px', color: '#666' }}>
                    {u.last_login ? new Date(u.last_login).toLocaleDateString('en-UG', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Never'}
                  </td>

                  <td style={{ padding: '12px' }}>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {!readOnly && u.id !== currentUserId && editRole !== u.id && (
                        <button onClick={() => { setEditRole(u.id); setNewRole(u.role); setResetPwd(null); }} style={smBtn('#e67e22')}>Change Role</button>
                      )}
                      {!readOnly && u.role !== 'member' && editMember !== u.id && (
                        <button onClick={() => { setEditMember(u.id); setNewMemberId(u.member_id || ''); setEditRole(null); setResetPwd(null); }} style={smBtn('#8e44ad')}>
                          {u.member_id ? 'Change Member Link' : 'Link Member'}
                        </button>
                      )}
                      {editMember === u.id && (
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <select value={newMemberId} onChange={e => setNewMemberId(e.target.value)} style={inlineInp}>
                            <option value="">-- None --</option>
                            {members.map(m => (
                              <option key={m.id} value={m.id}>{m.full_name} ({m.member_number})</option>
                            ))}
                          </select>
                          {!readOnly && <button onClick={() => linkMember(u.id)} style={smBtn('#27ae60')}>Save</button>}
                          <button onClick={() => { setEditMember(null); setNewMemberId(''); }} style={smBtn('#95a5a6')}>✕</button>
                        </div>
                      )}
                      {!readOnly && (resetPwd !== u.id ? (
                        <button onClick={() => { setResetPwd(u.id); setNewPwd(''); setConfirmPwd(''); setShowPwd(false); setShowConfirmPwd(false); setEditRole(null); }} style={smBtn('#2980b9')}>Reset Password</button>
                      ) : (
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                          <div style={{ position: 'relative' }}>
                            <input
                              type={showPwd ? 'text' : 'password'}
                              placeholder="New password"
                              value={newPwd}
                              onChange={e => setNewPwd(e.target.value)}
                              style={{ ...inlineInp, width: '120px', paddingRight: '28px' }}
                            />
                            <span onClick={() => setShowPwd(!showPwd)} style={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', fontSize: '14px', color: '#666' }}>
                              {showPwd ? '🙈' : '👁'}
                            </span>
                          </div>
                          <div style={{ position: 'relative' }}>
                            <input
                              type={showConfirmPwd ? 'text' : 'password'}
                              placeholder="Confirm"
                              value={confirmPwd}
                              onChange={e => setConfirmPwd(e.target.value)}
                              style={{ ...inlineInp, width: '100px', paddingRight: '28px' }}
                            />
                            <span onClick={() => setShowConfirmPwd(!showConfirmPwd)} style={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', fontSize: '14px', color: '#666' }}>
                              {showConfirmPwd ? '🙈' : '👁'}
                            </span>
                          </div>
                          <button onClick={() => resetPassword(u.id, u.username)} style={smBtn('#27ae60')}>Save</button>
                          <button onClick={() => { setResetPwd(null); setNewPwd(''); setConfirmPwd(''); }} style={smBtn('#95a5a6')}>✕</button>
                        </div>
                      ))}
                      {!readOnly && u.id !== currentUserId && (
                        <button onClick={() => toggleStatus(u.id, u.username, u.is_active !== false)} style={smBtn(u.is_active !== false ? '#f39c12' : '#27ae60')}>
                          {u.is_active !== false ? 'Deactivate' : 'Activate'}
                        </button>
                      )}
                      {!readOnly && isOfficer(u.role) && (
                        <button onClick={() => { setSigRow(sigRow === u.id ? null : u.id); if (sigFileRef.current) sigFileRef.current.value = ''; }} style={smBtn(sigRow === u.id ? '#95a5a6' : '#16a085')}>
                          {u.signature_image ? '✎ Signature' : '+ Signature'}
                        </button>
                      )}
                      {!readOnly && u.id !== currentUserId && (
                        <button onClick={() => deleteUser(u.id, u.username)} style={{ background: '#fde8e8', color: '#c0392b', border: '1px solid #f5c6cb', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>

                {/* ── Signature Panel ── */}
                {sigRow === u.id && isOfficer(u.role) && (
                  <tr key={`sig-${u.id}`} style={{ background: '#f0fff8', borderBottom: '1px solid #a9dfbf' }}>
                    <td colSpan="7" style={{ padding: '16px 20px' }}>
                      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                        <div>
                          <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#2c3e50', marginBottom: '6px' }}>
                            ✍ Officer Signature — {u.username} ({OFFICER_TITLES[u.role]})
                          </div>
                          <div style={{ fontSize: '11px', color: '#7f8c8d', marginBottom: '10px' }}>
                            Upload a clear image of the officer's signature (JPG/PNG, max 1.5MB). This will auto-appear on all generated PDF documents.
                          </div>
                          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <input ref={sigFileRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp"
                              style={{ fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px', padding: '4px 8px', background: 'white' }} />
                            {!readOnly && <button onClick={() => uploadSignature(u.id, u.username)} disabled={sigUploading}
                              style={{ ...smBtn('#16a085'), opacity: sigUploading ? 0.6 : 1, cursor: sigUploading ? 'not-allowed' : 'pointer' }}>
                              {sigUploading ? 'Saving...' : '💾 Save Signature'}
                            </button>}
                            {!readOnly && u.signature_image && (
                              <button onClick={() => removeSignature(u.id, u.username)} style={smBtn('#e74c3c')}>
                                🗑 Remove
                              </button>
                            )}
                          </div>
                        </div>

                        {u.signature_image && (
                          <div style={{ borderLeft: '1px solid #a9dfbf', paddingLeft: '20px' }}>
                            <div style={{ fontSize: '11px', color: '#7f8c8d', marginBottom: '6px' }}>Current Signature Preview</div>
                            <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '4px', padding: '8px', display: 'inline-block' }}>
                              <img src={u.signature_image} alt="Signature" style={{ maxWidth: '180px', maxHeight: '60px', objectFit: 'contain' }} />
                            </div>
                            <div style={{ fontSize: '11px', color: '#27ae60', marginTop: '4px' }}>✓ Signature on file</div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: '16px', padding: '10px 14px', background: '#fef9e7', borderLeft: '4px solid #f39c12', borderRadius: '4px', fontSize: '12px', color: '#7d6608' }}>
        <strong>Security Note:</strong> You cannot delete or deactivate your own account. Role changes take effect on next login. Always share new passwords via a secure private channel — never in a group message.
      </div>
    </div>
  );
}